import { ref, watch, type Ref } from "vue";
import api from "../api";
import type { MapNode } from "../types/nodes";
import type { RouteMapItem } from "../types/routes";
import { nearestNodeToTrack } from "../utils/nearestStation";
import { isFreshReading } from "../utils/readingFreshness";
import { stationAlerts, type StationAlert } from "../utils/stationAlerts";

export interface ConditionsNow {
  nodeName: string;
  km: number;
  tempC: number | null;
  windMs: number | null;
  alerts: StationAlert[];
}

// Кэш /nodes на 10 минут: нод мало, но показания внутри меняются - «сейчас»
// не должно опираться на запрос часовой давности.
const NODES_TTL_MS = 10 * 60 * 1000;
let nodesPromise: Promise<MapNode[]> | null = null;
let nodesFetchedAt = 0;
const loadNodes = () => {
  if (nodesPromise && Date.now() - nodesFetchedAt > NODES_TTL_MS) nodesPromise = null;
  if (!nodesPromise) nodesFetchedAt = Date.now();
  nodesPromise ??= api
    .get<MapNode[]>("/nodes")
    .then((r) => r.data || [])
    .catch(() => {
      nodesPromise = null;
      return [];
    });
  return nodesPromise;
};

// «Условия сейчас» для маршрута: свежая станция не дальше 2 км от трека.
// Иначе null (чип скрыт) - на проде это 2 маршрута из 22.
export function useConditionsNow(route: Ref<RouteMapItem | null>) {
  const conditions = ref<ConditionsNow | null>(null);

  watch(
    () => route.value?.coordinates,
    async (coords) => {
      if (!coords?.length) {
        conditions.value = null;
        return;
      }
      // Только станции со свежими показаниями: устаревшие данные не выдаём за «сейчас».
      const nodes = (await loadNodes()).filter((n) => isFreshReading(n.station?.latest?.polled_at, Date.now()));
      const hit = nearestNodeToTrack(nodes, coords);
      const reading = hit?.node.station?.latest;
      conditions.value = hit && reading
        ? {
            nodeName: hit.node.name,
            km: hit.km,
            tempC: reading.temp_c ?? null,
            windMs: reading.wind_speed_ms ?? null,
            alerts: stationAlerts(reading),
          }
        : null;
    },
    { immediate: true }
  );

  return { conditions };
}
